import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { KpiCard } from '@/components/common/KpiCard';
import { RiskBadge, RiskDot } from '@/components/common/RiskBadge';
import { CategoryBadge } from '@/components/common/CategoryBadge';
import { ObservationStageBadge } from '@/components/common/ObservationStageBadge';
import { EvidenceBadge } from '@/components/common/EvidenceBadge';
import { SimulationDisclaimer } from '@/components/common/SimulationDisclaimer';
import { AssetMap } from '@/components/map/AssetMap';
import { BeforeAfterViewer } from '@/components/detection/BeforeAfterViewer';
import { Wordmark, LogoMark } from '@/components/brand/Logo';
import { api } from '@/lib/api';
import { fmtDate, fmtRel, fmtCoord } from '@/lib/format';
import { fmtDistance, fmtFreshness } from '@/lib/format2';
import { useAuth, roleLabels } from '@/lib/auth';
import { PageLoader } from '@/components/common/Layout';
import { ArrowRight, ArrowLeft, Maximize2, X, LayoutGrid, Landmark,
         ScanSearch, Workflow, FileText, ClipboardCheck, ChevronRight } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

/**
 * Presentation Mode - executive command flow for demonstrations to senior
 * officials. Hides editing controls, uses larger typography, guided sequence.
 */
export default function PresentationModePage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(null);
  const [org, setOrg] = useState(null);
  usePageTitle('Presentation Mode');

  useEffect(() => {
    (async () => {
      try {
        const [ovv, recent, assets, alerts, incidents] = await Promise.all([
          api.get('/dashboard/overview').then(r => r.data),
          api.get('/dashboard/recent').then(r => r.data),
          api.get('/assets').then(r => r.data),
          api.get('/alerts', { params: { limit: 20 } }).then(r => r.data),
          api.get('/incidents', { params: { limit: 20 } }).then(r => r.data),
        ]);
        // primary asset = first (demo asset, or first available)
        const primary = assets[0];
        let zones = [], obs = [], detections = [];
        if (primary) {
          [zones, obs, detections] = await Promise.all([
            api.get('/zones', { params: { asset_id: primary.id } }).then(r => r.data),
            api.get('/observations', { params: { asset_id: primary.id, limit: 10 } }).then(r => r.data),
            api.get('/detections', { params: { asset_id: primary.id, limit: 10 } }).then(r => r.data),
          ]);
        }
        // Pick the "flagship" detection for evidence view (highest risk, confirmed)
        const flagship = detections.slice().sort((a, b) =>
          (b.risk_score || 0) - (a.risk_score || 0))[0];
        let baseImg = null, newImg = null;
        if (flagship) {
          const [b, n] = await Promise.all([
            api.get(`/observations/${flagship.baseline_observation_id}`, { params: { include_image: true } }).then(r => r.data),
            api.get(`/observations/${flagship.new_observation_id}`, { params: { include_image: true } }).then(r => r.data),
          ]);
          baseImg = b.image_b64; newImg = n.image_b64;
        }

        setData({ ovv, recent, assets, alerts, incidents, primary, zones, obs, detections,
                  flagship, baseImg, newImg });

        if (user?.org_id) {
          const o = await api.get(`/organizations/${user.org_id}`).then(r => r.data).catch(() => null);
          setOrg(o);
        }
      } catch (e) { /* noop */ }
    })();
  }, [user]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setStep(s => Math.min(4, s + 1));
      if (e.key === 'ArrowLeft') setStep(s => Math.max(0, s - 1));
      if (e.key === 'Escape') nav('/app/dashboard');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nav]);

  const enterFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-200"><PageLoader label="Preparing executive presentation…" /></div>;

  const steps = [
    { key: 'dashboard', title: 'Executive Command Dashboard', icon: LayoutGrid },
    { key: 'asset', title: 'Asset Command View', icon: Landmark },
    { key: 'evidence', title: 'Before-and-After Analysis', icon: ScanSearch },
    { key: 'workflow', title: 'Alert-to-Incident Workflow', icon: Workflow },
    { key: 'report', title: 'Management Summary', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col dark">
      {/* Top ribbon */}
      <div className="border-b border-slate-800 bg-slate-900/70 backdrop-blur">
        <div className="max-w-[1800px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <LogoMark size={26} className="text-teal-400" />
            <div className="leading-tight">
              <div className="font-display font-semibold text-[15px]">Aakash Drishti</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Presentation Mode</div>
            </div>
            {org?.is_demonstration && (
              <span className="hidden md:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded px-2 py-0.5">Demonstration environment</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={enterFullscreen} className="text-slate-300 hover:text-white" data-testid="presentation-fullscreen"><Maximize2 size={14} className="mr-1" /> Fullscreen</Button>
            <Button variant="ghost" size="sm" onClick={() => nav('/app/dashboard')} className="text-slate-300 hover:text-white" data-testid="presentation-exit"><X size={14} className="mr-1" /> Exit</Button>
          </div>
        </div>
      </div>

      {/* Step nav */}
      <div className="border-b border-slate-800">
        <div className="max-w-[1800px] mx-auto px-6 py-3 flex items-center gap-2 overflow-x-auto">
          {steps.map((s, i) => (
            <button key={s.key} onClick={() => setStep(i)}
              data-testid={`presentation-step-${i}`}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs border transition-all ${
                i === step ? 'bg-teal-500/15 border-teal-400 text-teal-200'
                : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200'
              }`}>
              <s.icon size={14} />
              <span>Screen {i + 1} · {s.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-[1800px] w-full mx-auto px-6 py-6" data-testid={`presentation-screen-${steps[step].key}`}>
        {step === 0 && <ExecutiveDashboard {...data} />}
        {step === 1 && <AssetCommand {...data} />}
        {step === 2 && <EvidenceView {...data} />}
        {step === 3 && <WorkflowView {...data} />}
        {step === 4 && <ManagementSummary {...data} />}
      </div>

      {/* Footer nav */}
      <div className="border-t border-slate-800 bg-slate-900/70 backdrop-blur">
        <div className="max-w-[1800px] mx-auto px-6 h-12 flex items-center justify-between text-xs">
          <div className="text-slate-400">
            Screen <span className="text-slate-100 font-mono">{step + 1}/5</span> · {steps[step].title}
            <span className="mx-2 text-slate-600">·</span>
            <span className="text-slate-500">Use ← / → to navigate</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="border-slate-700 text-slate-200 hover:bg-slate-800">
              <ArrowLeft size={12} className="mr-1" /> Previous
            </Button>
            <Button size="sm" onClick={() => setStep(Math.min(4, step + 1))} disabled={step === 4} data-testid="presentation-next">
              Next <ArrowRight size={12} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Screen 1: Executive Dashboard ----------------
function ExecutiveDashboard({ ovv, recent, assets }) {
  const latestObs = recent.observations?.[0];
  const freshness = fmtFreshness(latestObs?.acquired_at);
  return (
    <div className="space-y-5">
      <SectionHeader kicker="Screen 1" title="Executive Command Dashboard"
        subtitle="Portfolio-wide status across all monitored assets." />

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <BigKpi label="Monitored assets" value={ovv.total_assets} />
        <BigKpi label="Active alerts" value={ovv.active_alerts} tone="warn" />
        <BigKpi label="Critical alerts" value={ovv.critical_alerts} tone="danger" />
        <BigKpi label="Open incidents" value={ovv.open_incidents} tone="info" />
        <BigKpi label="Portfolio risk" value={ovv.portfolio_risk_avg} suffix="/100" tone={ovv.portfolio_risk_avg > 60 ? 'danger' : ovv.portfolio_risk_avg > 35 ? 'warn' : 'ok'} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 bg-slate-900/60 border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Recent alerts requiring attention</div>
            <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400">org-scoped</Badge>
          </div>
          <div className="divide-y divide-slate-800">
            {(recent.alerts || []).slice(0, 6).map(a => (
              <div key={a.id} className="flex items-center gap-3 py-2.5">
                <RiskDot severity={a.severity} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.title}</div>
                  <div className="text-xs text-slate-400">{fmtRel(a.created_at)} · {a.status}</div>
                </div>
                <CategoryBadge category={a.category} />
                <RiskBadge severity={a.severity} />
              </div>
            ))}
            {recent.alerts?.length === 0 && <div className="py-6 text-center text-sm text-slate-500">No active alerts.</div>}
          </div>
        </Card>
        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <div className="text-sm font-semibold">Observation freshness</div>
          <div className="mt-3 text-3xl font-display font-semibold tabular-nums">
            {latestObs ? freshness.label : '—'}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {latestObs ? `Latest usable observation processed ${fmtDate(latestObs.acquired_at)}.` : 'No observation available.'}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-800 text-xs text-slate-500">
            Data freshness reflects the most recent satellite observation ingested and processed by Aakash Drishti. Observation frequency depends on satellite coverage.
          </div>
        </Card>
      </div>

      <Card className="p-5 bg-slate-900/60 border-slate-800">
        <div className="text-sm font-semibold">Monitored assets</div>
        <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {assets.slice(0, 6).map(a => (
            <div key={a.id} className="rounded-lg border border-slate-800 p-3 bg-slate-950/40">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{a.category}</div>
              <div className="mt-1 font-medium">{a.name}</div>
              <div className="text-xs text-slate-400 mt-0.5">{a.location_name}</div>
              <div className="mt-2 flex items-center gap-2">
                <RiskBadge severity={a.current_risk_level} />
                {a.is_demonstration && <SimulationDisclaimer compact />}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ---------------- Screen 2: Asset Command ----------------
function AssetCommand({ primary, zones, alerts, incidents }) {
  if (!primary) return <EmptyScreen label="No asset available for presentation." />;
  return (
    <div className="space-y-5">
      <SectionHeader kicker="Screen 2" title="Asset Command View"
        subtitle={<><span>{primary.name}</span>{primary.is_demonstration && <> · <SimulationDisclaimer compact /></>}</>} />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card className="p-0 overflow-hidden bg-slate-900/60 border-slate-800">
            <AssetMap
              asset={primary}
              zones={zones}
              subAssets={primary.sub_assets || []}
              alerts={alerts.filter(a => a.asset_id === primary.id)}
              incidents={incidents.filter(i => i.asset_id === primary.id)}
              height={560}
              showLegend={true}
            />
          </Card>
        </div>
        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <div className="text-sm font-semibold">Asset profile</div>
          <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
            <Row k="Category" v={primary.category} />
            <Row k="Criticality" v={primary.criticality} />
            <Row k="Current risk" v={<RiskBadge severity={primary.current_risk_level} />} />
            <Row k="Location" v={`${fmtCoord(primary.lat)}, ${fmtCoord(primary.lng)}`} />
            <Row k="Zones" v={<span className="font-mono">{zones.length}</span>} />
            <Row k="Sub-assets" v={<span className="font-mono">{(primary.sub_assets || []).length}</span>} />
          </div>
          {primary.hydropower && (
            <div className="mt-3 pt-3 border-t border-slate-800 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-slate-400">Dam type</span><span>{primary.hydropower.dam_type}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Dam height</span><span className="font-mono">{primary.hydropower.dam_height_m} m</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Installed capacity</span><span className="font-mono">{primary.hydropower.installed_capacity_mw} MW</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Reservoir</span><span className="font-mono">{primary.hydropower.reservoir_capacity_mcm} MCM</span></div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ---------------- Screen 3: Evidence View ----------------
function EvidenceView({ flagship, baseImg, newImg }) {
  if (!flagship || !baseImg || !newImg) return <EmptyScreen label="No evidence pair available." />;
  return (
    <div className="space-y-5">
      <SectionHeader kicker="Screen 3" title="Before-and-After Analysis"
        subtitle="AI-detected change with pixel-level overlay and analyst context." />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <BeforeAfterViewer
            before={baseImg}
            after={newImg}
            overlayB64={flagship.overlay_jpg_b64}
            meta={{ baseline: null, after: null, source: 'Simulated observation', resolution: 10 }}
            detection={flagship}
          />
        </div>
        <div className="space-y-4">
          <Card className="p-5 bg-slate-900/60 border-slate-800">
            <div className="text-sm font-semibold">AI interpretation</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <CategoryBadge category={flagship.change_type} />
              <RiskBadge severity={flagship.severity} />
              <EvidenceBadge quality={flagship.evidence_quality} />
              <ObservationStageBadge stage={flagship.observation_stage} />
            </div>
            <p className="mt-3 text-sm">{flagship.description}</p>
            {flagship.recommended_action && (
              <div className="mt-3 text-xs"><b>Recommended:</b> <span className="text-slate-300">{flagship.recommended_action}</span></div>
            )}
          </Card>
          <Card className="p-5 bg-slate-900/60 border-slate-800">
            <div className="text-sm font-semibold">Scoring & context</div>
            <div className="mt-3 space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">AI confidence</span><span className="font-mono">{Math.round((flagship.llm_confidence || 0) * 100)}%</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Risk score</span><span className="font-mono">{flagship.risk_score}/100</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Affected area</span><span className="font-mono">{flagship.affected_area_pct}%</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Distance to asset</span><span className="font-mono">{fmtDistance(flagship.distance_to_asset_m)}</span></div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ---------------- Screen 4: Workflow ----------------
function WorkflowView({ alerts, incidents, recent }) {
  const flow = [
    { key: 'AI Observation', count: (recent.observations || []).length, hint: 'Ingested satellite scenes' },
    { key: 'Under Analyst Review', count: (recent.alerts || []).filter(a => a.status === 'Under review').length, hint: 'Analysts assessing observations' },
    { key: 'Validated Alert', count: (recent.alerts || []).filter(a => a.status === 'Validated' || a.status === 'Escalated').length, hint: 'Confirmed by analyst' },
    { key: 'Confirmed Incident', count: (incidents || []).filter(i => i.status && !['Closed', 'Resolved'].includes(i.status)).length, hint: 'Assigned & in action' },
    { key: 'Resolved / Closed', count: (incidents || []).filter(i => ['Closed', 'Resolved'].includes(i.status)).length, hint: 'Closure recorded' },
  ];
  return (
    <div className="space-y-5">
      <SectionHeader kicker="Screen 4" title="Alert-to-Incident Workflow"
        subtitle="Observation → analyst validation → alert → incident → field verification → closure." />

      <div className="grid md:grid-cols-5 gap-3">
        {flow.map((s, i) => (
          <Card key={s.key} className="p-4 bg-slate-900/60 border-slate-800">
            <div className="flex items-center justify-between">
              <ObservationStageBadge stage={s.key} />
              <span className="text-[10px] text-slate-500">Step {i + 1}</span>
            </div>
            <div className="mt-3 text-4xl font-mono tabular-nums font-semibold">{s.count}</div>
            <div className="mt-1 text-xs text-slate-400">{s.hint}</div>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <div className="text-sm font-semibold mb-3">Active alerts</div>
          <div className="divide-y divide-slate-800">
            {(alerts || []).slice(0, 6).map(a => (
              <div key={a.id} className="py-2 flex items-center gap-3">
                <RiskDot severity={a.severity} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{a.title}</div>
                  <div className="text-[10px] text-slate-500">{fmtRel(a.created_at)} · {a.status}</div>
                </div>
                <CategoryBadge category={a.category} />
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <div className="text-sm font-semibold mb-3">Incidents in action</div>
          <div className="divide-y divide-slate-800">
            {(incidents || []).slice(0, 6).map(i => (
              <div key={i.id} className="py-2 flex items-center gap-3">
                <span className="font-mono text-xs text-slate-500 w-24">{i.number}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{i.title}</div>
                  <div className="text-[10px] text-slate-500">{i.status}</div>
                </div>
                <RiskBadge severity={i.severity} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ---------------- Screen 5: Management Summary ----------------
function ManagementSummary({ ovv, primary, incidents }) {
  const nav = useNavigate();
  return (
    <div className="space-y-5">
      <SectionHeader kicker="Screen 5" title="Management Summary"
        subtitle="Executive brief consolidating risks, response actions and recommendations." />

      <Card className="p-6 bg-slate-900/60 border-slate-800">
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Portfolio</div>
            <div className="mt-1 text-3xl font-display font-semibold">{ovv.total_assets} <span className="text-slate-500 text-lg">assets</span></div>
            <div className="text-xs text-slate-400 mt-1">Portfolio risk: <span className="text-slate-200 font-mono">{ovv.portfolio_risk_avg}/100</span></div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Alerts</div>
            <div className="mt-1 text-3xl font-display font-semibold">{ovv.active_alerts}</div>
            <div className="text-xs text-slate-400 mt-1">{ovv.critical_alerts} critical · {ovv.validated_detections} validated by analyst</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Incidents</div>
            <div className="mt-1 text-3xl font-display font-semibold">{ovv.open_incidents}</div>
            <div className="text-xs text-slate-400 mt-1">Open across responsible departments</div>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <div className="text-sm font-semibold">Recommended decisions</div>
          <ul className="mt-3 space-y-2 text-sm text-slate-300 list-disc pl-5">
            <li>Prioritise field verification of critical incidents within 24 hours.</li>
            <li>Confirm access-road integrity to project structures.</li>
            <li>Review protection-zone encroachment against approved land-use records.</li>
            <li>Continue observation for water-spread and terrain change over next acquisition cycle.</li>
          </ul>
        </Card>
        <Card className="p-5 bg-slate-900/60 border-slate-800">
          <div className="text-sm font-semibold">Reports & audit</div>
          <div className="mt-3 space-y-2">
            <Button variant="outline" className="w-full justify-between border-slate-700 text-slate-200"
                    onClick={() => nav('/app/reports')}>Generate PDF reports <ChevronRight size={14} /></Button>
            <Button variant="outline" className="w-full justify-between border-slate-700 text-slate-200"
                    onClick={() => nav('/app/audit')}>View audit trail <ChevronRight size={14} /></Button>
            <Button variant="outline" className="w-full justify-between border-slate-700 text-slate-200"
                    onClick={() => nav('/app/incidents')}>Open incident register <ChevronRight size={14} /></Button>
          </div>
        </Card>
      </div>

      <SimulationDisclaimer />
    </div>
  );
}

function SectionHeader({ kicker, title, subtitle }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-teal-400 font-semibold">{kicker}</div>
      <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight">{title}</h1>
      <div className="mt-2 text-sm text-slate-400">{subtitle}</div>
    </div>
  );
}
function EmptyScreen({ label }) { return <div className="text-slate-400 text-sm p-10 text-center">{label}</div>; }
function Row({ k, v }) { return (<><div className="text-slate-400">{k}</div><div className="text-right">{v}</div></>); }

function BigKpi({ label, value, suffix, tone = 'default' }) {
  const toneCls = { default: 'text-slate-100', danger: 'text-red-400', warn: 'text-amber-300', ok: 'text-emerald-300', info: 'text-teal-300' }[tone];
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className={`mt-2 font-mono tabular-nums text-4xl font-semibold ${toneCls}`}>
        {value}{suffix && <span className="text-slate-500 text-lg">{suffix}</span>}
      </div>
    </div>
  );
}
